import { useEffect, useRef, useState } from "react";
import { TopShopNotAppliedCard } from "@/components/topshop/TopShopNotAppliedCard";
import { Link } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import { ImagePlus, Loader2, Pencil, Trash2 } from "lucide-react";
import TopShopApplicationForm from "@/components/topshop/TopShopApplicationForm";
import { toast } from "sonner";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth";
import { useSignedStorageUrl } from "@/hooks/useSignedStorageUrl";
import {
  IMPACT_LABELS, MAX_PHOTOS, TopShopStatusBadge, resizeToJpeg, useMyTopShopApplication,
} from "@/components/topshop/topShopShared";

const BUCKET = "top-shop-photos";
const ACCEPT = ["image/jpeg", "image/png", "image/webp"];

function PhotoThumb({ photo, onDelete }: { photo: { id: string; storage_path: string }; onDelete: () => Promise<void> }) {
  // Hook's BucketName type doesn't list this bucket; the runtime call is generic.
  const { url, loading } = useSignedStorageUrl(BUCKET as any, photo.storage_path, 3600);
  const [deleting, setDeleting] = useState(false);
  return (
    <div className="relative aspect-square overflow-hidden rounded-md border bg-muted">
      {loading || !url ? (
        <div className="flex h-full items-center justify-center"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
      ) : (
        <img src={url} alt="Photo of your shop" className="h-full w-full object-cover" />
      )}
      <AlertDialog>
        <AlertDialogTrigger asChild>
          <Button type="button" size="icon" variant="destructive" className="absolute right-1 top-1 h-8 w-8" aria-label="Delete photo" disabled={deleting}>
            {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
          </Button>
        </AlertDialogTrigger>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this photo?</AlertDialogTitle>
            <AlertDialogDescription>This photo will be removed from your application.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={async () => { setDeleting(true); try { await onDelete(); } finally { setDeleting(false); } }}>
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export default function TopShopApplication() {
  useEffect(() => {
    document.title = "Top Shop Application | EAB Connect";
  }, []);
  return (
    <DashboardLayout>
      <div className="mx-auto max-w-3xl space-y-6">
        <h1 className="text-2xl font-bold">Top Shop Challenge</h1>
        <TopShopApplicationPanel />
      </div>
    </DashboardLayout>
  );
}

/** Application summary and photo manager, without page chrome. */
export function TopShopApplicationPanel() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { data, isLoading, error } = useMyTopShopApplication();
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploads, setUploads] = useState<{ name: string; done: boolean }[]>([]);
  const [editing, setEditing] = useState(false);


  const refresh = () => queryClient.invalidateQueries({ queryKey: ["top-shop", "my-application", user?.id] });

  const handleFiles = async (fileList: FileList | null) => {
    if (!fileList || !data || !user) return;
    const app = data.application;
    const remaining = MAX_PHOTOS - data.photos.length;
    let files = Array.from(fileList).filter((f) => {
      if (!ACCEPT.includes(f.type)) { toast.error(`${f.name} is not a JPEG, PNG or WebP image.`); return false; }
      return true;
    });
    if (files.length > remaining) {
      toast.error(`You can add ${remaining} more photo${remaining === 1 ? "" : "s"}. Only the first ${remaining} will be uploaded.`);
      files = files.slice(0, remaining);
    }
    if (inputRef.current) inputRef.current.value = "";
    if (!files.length) return;

    setUploads(files.map((f) => ({ name: f.name, done: false })));
    const maxSort = data.photos.reduce((m: number, p: any) => Math.max(m, p.sort_order ?? 0), -1);
    let next = maxSort + 1;

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      try {
        const blob = await resizeToJpeg(file);
        const path = `${user.id}/${app.id}/${crypto.randomUUID()}.jpg`;
        const { error: upErr } = await supabase.storage.from(BUCKET).upload(path, blob, { contentType: "image/jpeg" });
        if (upErr) throw upErr;
        const { error: insErr } = await supabase.from("program_application_photos").insert({
          application_id: app.id, storage_path: path, sort_order: next++,
        });
        if (insErr) {
          await supabase.storage.from(BUCKET).remove([path]);
          throw insErr;
        }
      } catch (e: any) {
        toast.error(`Could not upload ${file.name}: ${e?.message ?? "unknown error"}`);
      } finally {
        setUploads((u) => u.map((x, idx) => (idx === i ? { ...x, done: true } : x)));
      }
    }
    await refresh();
    setUploads([]);
  };

  const deletePhoto = async (photo: { id: string; storage_path: string }) => {
    const { error: sErr } = await supabase.storage.from(BUCKET).remove([photo.storage_path]);
    if (sErr) { toast.error("Could not delete the photo. Please try again."); return; }
    const { error: dErr } = await supabase.from("program_application_photos").delete().eq("id", photo.id);
    if (dErr) { toast.error("Could not delete the photo. Please try again."); return; }
    await refresh();
  };

  const uploading = uploads.length > 0;

  return (
      <div className="space-y-6">
        {isLoading ? (
          <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>
        ) : error ? (
          <Card><CardContent className="p-6 text-sm text-destructive">We could not load your application. Please refresh the page.</CardContent></Card>
        ) : !data ? (
          <TopShopNotAppliedCard />
        ) : (
          <>
            {editing && data.application.status === "submitted" ? (
              <Card>
                <CardContent className="p-6">
                  <TopShopApplicationForm mode="edit" application={data.application}
                    onDone={() => setEditing(false)} onCancel={() => setEditing(false)} />
                </CardContent>
              </Card>
            ) : (
            <Card>
              <CardHeader>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <CardTitle>{data.application.business_name}</CardTitle>
                  <div className="flex items-center gap-2">
                    <TopShopStatusBadge status={data.application.status} />
                    {data.application.status === "submitted" && (
                      <Button type="button" size="sm" variant="outline" onClick={() => setEditing(true)}>
                        <Pencil className="mr-2 h-4 w-4" />Edit application
                      </Button>
                    )}
                  </div>
                </div>
                {data.application.status !== "submitted" && (
                  <p className="text-sm text-muted-foreground">
                    Your application has been reviewed. To make a change, contact EAB at{" "}
                    <a href="https://eaborders.org" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">eaborders.org</a>.
                  </p>
                )}
                {data.application.status === "accepted" && (
                  <p className="text-sm text-[#22891D] font-medium">Congratulations, you have been accepted into the Top Shop Challenge. We will contact you with next steps.</p>
                )}
                {data.application.status === "completed" && (
                  <p className="text-sm text-[#14532D] font-medium">Congratulations, you completed the Top Shop Challenge. Keep learning and growing on EAB Connect.</p>
                )}
                {data.application.status === "withdrawn" && (
                  <p className="text-sm text-muted-foreground">Thank you for taking part. You still have full access to EAB Connect training and mentors.</p>
                )}
                {data.application.status === "waitlisted" && (
                  <p className="text-sm text-muted-foreground">You are on the waitlist. We will let you know if a place opens up.</p>
                )}
              </CardHeader>
              <CardContent>
                <dl className="grid gap-3 sm:grid-cols-2 text-sm">
                  <div><dt className="text-muted-foreground">Parish</dt><dd className="font-medium">{data.application.parish}</dd></div>
                  {data.application.community?.name && (
                    <div><dt className="text-muted-foreground">Community</dt><dd className="font-medium">{data.application.community.name}</dd></div>
                  )}
                  <div><dt className="text-muted-foreground">Type of shop</dt><dd className="font-medium">{data.application.business_type?.name ?? "Not set"}</dd></div>
                  <div><dt className="text-muted-foreground">Hurricane impact</dt><dd className="font-medium">{IMPACT_LABELS[data.application.hurricane_impact_level] ?? data.application.hurricane_impact_level}</dd></div>
                  <div><dt className="text-muted-foreground">Date applied</dt><dd className="font-medium">{format(new Date(data.application.created_at), "MMMM d, yyyy")}</dd></div>
                  <div className="sm:col-span-2"><dt className="text-muted-foreground">Focus areas</dt><dd className="font-medium">{data.focusAreaNames.join(", ")}</dd></div>
                  <div className="sm:col-span-2"><dt className="text-muted-foreground">Why you want to join</dt><dd className="font-medium whitespace-pre-line">{data.application.why_join ?? "Not provided (applied before this question was added)"}</dd></div>
                </dl>
              </CardContent>
            </Card>
            )}

            <Card>
              <CardHeader>
                <CardTitle>Photos of your shop</CardTitle>
                <CardDescription>Add up to 5 photos of your shop, stall or stand. Clear photos of the outside and inside help us understand your business.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {data.photos.length > 0 && (
                  <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                    {data.photos.map((p: any) => <PhotoThumb key={p.id} photo={p} onDelete={() => deletePhoto(p)} />)}
                  </div>
                )}
                {uploading && (
                  <ul className="space-y-1 text-sm" aria-live="polite">
                    {uploads.map((u, i) => (
                      <li key={i} className="flex items-center gap-2">
                        {u.done ? <span className="text-[#22891D]">Done</span> : <Loader2 className="h-4 w-4 animate-spin" />}
                        <span className="truncate">{u.name}</span>
                      </li>
                    ))}
                  </ul>
                )}
                <input ref={inputRef} type="file" accept={ACCEPT.join(",")} multiple className="hidden" onChange={(e) => handleFiles(e.target.files)} />
                <div className="flex items-center gap-3">
                  <Button type="button" variant="outline" disabled={uploading || data.photos.length >= MAX_PHOTOS} onClick={() => inputRef.current?.click()}>
                    {uploading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <ImagePlus className="mr-2 h-4 w-4" />}
                    {uploading ? "Uploading..." : "Add photos"}
                  </Button>
                  <span className="text-sm text-muted-foreground">{data.photos.length} of {MAX_PHOTOS}</span>
                </div>
              </CardContent>
            </Card>
          </>
        )}
      </div>
  );
}
